import React, { useState, useEffect, useRef } from 'react';
import { HiUsers, HiSearch, HiX } from 'react-icons/hi';
import { MdRefresh } from 'react-icons/md';
import ImageWithFallback from './ImageWithFallback';

const GhostUserSelector = ({ 
  userList, 
  isLoadingUsers, 
  onSelectUser, 
  onRefresh,
  currentUser
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setFilteredUsers(userList);
  }, [userList]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredUsers(userList);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = userList.filter(user =>
        (user.name || '').toLowerCase().includes(query) ||
        (user.email || '').toLowerCase().includes(query)
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, userList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelectUser = (user) => {
    onSelectUser(user);
    setIsOpen(false);
    setSearchQuery('');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleRefresh = (e) => {
    e.stopPropagation();
    onRefresh();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-canvas-primary text-white rounded-lg hover:bg-opacity-90 transition-all duration-200 border border-canvas-white border-opacity-20 no-underline hover:no-underline focus:no-underline focus:outline-none"
        title="Switch User (Ghost Mode)"
        aria-label="Switch user (ghost mode)"
        aria-expanded={isOpen}
      >
        <HiUsers className="w-5 h-5" />
        <span className="hidden md:inline text-sm font-medium">
          {currentUser ? currentUser.name || currentUser.email : 'Switch User'}
        </span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden flex flex-col">
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">Switch User</h3>
              <button
                onClick={handleRefresh}
                disabled={isLoadingUsers}
                className="p-1 hover:bg-gray-200 rounded transition-colors no-underline hover:no-underline focus:no-underline focus:outline-none"
                title="Refresh user list"
                aria-label="Refresh user list"
              >
                <MdRefresh className={`w-4 h-4 text-gray-600 ${isLoadingUsers ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <div className="relative">
              <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name or email..."
                aria-label="Search users by name or email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-canvas-primary focus:border-transparent"
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 no-underline hover:no-underline focus:no-underline focus:outline-none"
                >
                  <HiX className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {isLoadingUsers ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                Loading users...
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                {searchQuery ? 'No users found' : 'No users available'}
              </div>
            ) : (
              <ul className="py-1">
                {filteredUsers.map((user) => (
                  <li key={user.userId}>
                    <button
                      onClick={() => handleSelectUser(user)}
                      className="w-full px-3 py-2 hover:bg-gray-50 transition-colors flex items-center gap-3 text-left no-underline hover:no-underline focus:no-underline focus:outline-none"
                    >
                      <div className="flex-shrink-0">
                        {user.avatar ? (
                          <ImageWithFallback
                            src={user.avatar}
                            alt={user.name}
                            className="w-10 h-10 rounded-full object-cover"
                            fallback={
                              <div className="w-10 h-10 rounded-full bg-canvas-primary text-white flex items-center justify-center text-sm font-semibold">
                                {getInitials(user.name || user.email)}
                              </div>
                            }
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-canvas-primary text-white flex items-center justify-center text-sm font-semibold">
                            {getInitials(user.name || user.email)}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user.name || 'No Name'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {user.email}
                        </p>
                      </div>
                      {currentUser && currentUser.userId === user.userId && (
                        <div className="flex-shrink-0">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-canvas-primary text-white">
                            Current
                          </span>
                        </div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GhostUserSelector;
